/**
 * « Animation » d'une SKILLCARD — fiche auteur, admins (moteur JRAF, lot 3).
 *
 * « Créer l'animation (IA) » : l'IA explique le cours et la méthode de la
 * carte en une courte animation verticale (scènes HORA, partie « À toi »
 * payante), en tâche de fond (edge fn `animation-generate`). On suit la
 * génération ici, puis « Tester » ouvre le brouillon comme un élève.
 * « Corriger avec l'IA » part du brouillon actuel (tes corrections, tes voix :
 * gardées là où le texte ne change pas) et applique une consigne, sur toute
 * l'animation ou une scène — sans consigne, l'IA corrige les défauts mesurés.
 * « Régénérer » repart de zéro depuis la SKILLCARD ; « Reprendre » relance une
 * génération interrompue là où elle s'est arrêtée.
 */

import { useId, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  CheckCircle2,
  Clapperboard,
  Loader2,
  Play,
  RotateCcw,
  Sparkles,
  Wand2,
} from 'lucide-react';
import { isQualityWarning } from '@jeraisonne/animation';
import { Button, cn } from '@jeraisonne/ui';
import { AiModelSelect, CARD_DEFAULT_MODEL } from '@/features/cards/ai-model-select';
import type { PipelineModelId } from '@/lib/admin/pipeline-models';
import { AnimationVoicesPicker } from '@/features/animations/animation-voices-picker';
import { DEFAULT_VOICES, voicesBody, type VoicesChoice } from '@/lib/animations/voices';
import {
  GENERATION_STEP_LABELS,
  isGenerating,
  isGenerationStale,
  useCardAnimation,
  useGenerateAnimation,
  type AnimationRow,
} from '@/lib/animations/api';

export function CardAnimationPanel({ cardId }: { cardId: string }) {
  const query = useCardAnimation(cardId);
  const row = query.data ?? null;
  return (
    <section
      aria-labelledby="card-animation-title"
      className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4"
    >
      <div className="flex items-center gap-2">
        <Clapperboard className="text-primary-600 h-5 w-5" aria-hidden />
        <h2 id="card-animation-title" className="text-ink-900 font-display text-base font-bold">
          Animation
        </h2>
        <Link
          to="/tutor/admin/animations"
          className="text-primary-600 ml-auto inline-flex min-h-11 items-center text-[13px] font-semibold hover:underline"
        >
          Toutes les animations
        </Link>
      </div>
      {query.isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : query.error && !query.data ? (
        <p role="alert" className="text-error-600 text-sm">
          Impossible de lire l’animation de cette carte.
        </p>
      ) : (
        <AnimationGenerationControls row={row} cardId={cardId} showTestLink />
      )}
    </section>
  );
}

/** État de la génération et ses commandes (fiche de la carte, écran « Tester »). */
export function AnimationGenerationControls({
  row,
  cardId,
  showTestLink = false,
  testLabel = 'Tester',
  validity,
  compact = false,
  actions,
}: {
  row: AnimationRow | null;
  cardId: string;
  showTestLink?: boolean;
  /** texte du lien vers l'écran « Tester » */
  testLabel?: string;
  /** validation refaite sur place (écran « Tester ») ; sinon celle du serveur */
  validity?: { valid: boolean; errors: number; quality?: number };
  /** liste admin : sans la pastille de validité (la liste a les siennes), commandes sur une ligne */
  compact?: boolean;
  /** commandes en plus, sur la même ligne (liste admin : publier, supprimer) */
  actions?: React.ReactNode;
}) {
  const generate = useGenerateAnimation();
  const [instructions, setInstructions] = useState('');
  const [model, setModel] = useState<PipelineModelId>(CARD_DEFAULT_MODEL);
  /** monologue ou dialogue (rôles, consignes) */
  const [voices, setVoices] = useState<VoicesChoice>(DEFAULT_VOICES);
  /** formulaire ouvert : corriger le brouillon, ou tout refaire */
  const [mode, setMode] = useState<'revise' | 'regenerate' | null>(null);
  const [reviseText, setReviseText] = useState('');
  /** l'IA corrige aussi le rapport de la vérification (erreurs, défauts) */
  const [fixIssues, setFixIssues] = useState(true);
  const [scope, setScope] = useState('');
  const scopeId = useId();
  const open = mode === 'regenerate';
  const scenes = useMemo(() => draftScenes(row?.draft), [row?.draft]);
  // défauts que « Corriger les défauts » ferait réparer : ceux vus sur place, sinon ceux du serveur
  const defects = validity
    ? validity.errors + (validity.quality ?? 0)
    : (row?.validation?.errors.length ?? 0) +
      (row?.validation?.warnings.filter((w) => isQualityWarning(w)).length ?? 0);
  const running = isGenerating(row);
  const stale = row ? isGenerationStale(row) : false;
  const failed = row?.generation_status === 'failed' || stale;
  const valid = validity?.valid ?? row?.draft_valid ?? false;
  const errorCount = validity?.errors ?? row?.validation?.errors.length ?? 0;

  const create = () => {
    const v = voicesBody(voices);
    generate.mutate({
      card_id: cardId,
      ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
      ...(v ? { voices: v } : {}),
      model,
    });
  };

  const toggle = (m: 'revise' | 'regenerate') => setMode((cur) => (cur === m ? null : m));

  const revise = () => {
    if (!row) return;
    generate.mutate(
      {
        animation_id: row.id,
        revise: {
          ...(reviseText.trim() ? { instructions: reviseText.trim() } : {}),
          ...(scope ? { scene_id: scope } : {}),
          ...(reviseText.trim() && !fixIssues ? { fix_issues: false } : {}),
        },
        model,
      },
      {
        onSuccess: () => {
          setMode(null);
          setReviseText('');
        },
      },
    );
  };

  const reviseForm = (
    <div className="border-primary-200 bg-primary-50/50 space-y-2 rounded-xl border p-3">
      <p className="text-ink-600 text-[13px]">
        L’IA part de l’animation telle qu’elle est (tes corrections, ta voix) et ne change que ce
        que tu demandes.
      </p>
      <label className="block space-y-1">
        <span className="text-ink-700 text-xs font-semibold">Que faut-il changer ?</span>
        <textarea
          className="border-ink-200 text-ink-900 w-full rounded-lg border bg-white px-3 py-2 text-sm"
          rows={3}
          maxLength={2000}
          placeholder={
            defects
              ? `Laisse vide pour corriger ${defects > 1 ? `les ${defects} défauts signalés` : 'le défaut signalé'}. Ou : « mets le résultat en vert », « un exemple avec des décimaux »…`
              : '« Mets le résultat en vert », « ralentis le calcul », « un exemple avec des décimaux »…'
          }
          value={reviseText}
          onChange={(e) => setReviseText(e.target.value)}
        />
      </label>
      {defects > 0 && reviseText.trim() !== '' && (
        <label className="text-ink-700 flex min-h-11 items-start gap-2 text-[13px]">
          <input
            type="checkbox"
            className="accent-primary-500 mt-0.5 h-4 w-4"
            checked={fixIssues}
            onChange={(e) => setFixIssues(e.target.checked)}
          />
          <span>
            Corriger aussi{' '}
            {defects > 1 ? `les ${defects} problèmes signalés` : 'le problème signalé'} par la
            vérification (erreurs, mise en page, rythme, figure)
            {scope ? ' — ceux de cette scène' : ''}
          </span>
        </label>
      )}
      {scenes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={scopeId} className="text-ink-700 text-xs font-semibold">
            Où
          </label>
          <select
            id={scopeId}
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="border-ink-200 h-11 max-w-full rounded-lg border bg-white px-2 text-sm"
          >
            <option value="">Toute l’animation</option>
            {scenes.map((sc) => (
              <option key={sc.id} value={sc.id}>
                {sc.hora ? `${sc.hora} · ` : ''}
                {sc.title} (cette scène seulement)
              </option>
            ))}
          </select>
        </div>
      )}
      <AiModelSelect value={model} onChange={setModel} disabled={generate.isPending} />
      <Button
        fullWidth
        className="sm:w-auto"
        loading={generate.isPending}
        disabled={!reviseText.trim() && defects === 0}
        onClick={revise}
      >
        <span className="flex items-center justify-center gap-2">
          <Wand2 className="h-4 w-4" aria-hidden />
          {reviseText.trim() ? 'Corriger' : 'Corriger les défauts'}
        </span>
      </Button>
    </div>
  );

  const form = (label: string) => (
    <div className="space-y-2">
      <label className="block space-y-1">
        <span className="text-ink-600 text-xs font-semibold">Consignes (facultatif)</span>
        <textarea
          className="border-ink-200 text-ink-900 w-full rounded-lg border bg-white px-3 py-2 text-sm"
          rows={2}
          maxLength={2000}
          placeholder="Insiste sur la racine carrée, prends un exemple avec des décimaux…"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
      </label>
      <AnimationVoicesPicker value={voices} onChange={setVoices} disabled={generate.isPending} />
      {row?.draft != null && (
        <p className="text-ink-600 text-[13px]">
          Repart de zéro depuis la SKILLCARD : tes corrections et ta voix seront remplacées.
        </p>
      )}
      <AiModelSelect value={model} onChange={setModel} disabled={generate.isPending} />
      <Button fullWidth className="sm:w-auto" loading={generate.isPending} onClick={create}>
        <span className="flex items-center justify-center gap-2">
          <Sparkles className="h-4 w-4" aria-hidden />
          {label}
        </span>
      </Button>
    </div>
  );

  return (
    <div className="space-y-3">
      {generate.error && (
        <p role="alert" className="text-error-600 text-sm">
          {generate.error.message}
        </p>
      )}

      {!row && (
        <>
          <p className="text-ink-600 text-sm">
            L’IA explique le cours et la méthode de cette carte en une courte animation verticale
            (scènes HORA, partie « À toi » payante). Tu la testes, puis tu la publies.
          </p>
          {form('Créer l’animation (IA)')}
        </>
      )}

      {row && running && (
        <p role="status" className="text-ink-700 flex items-center gap-2 text-sm">
          <Loader2 className="text-primary-600 h-4 w-4 animate-spin" aria-hidden />
          <span>
            {GENERATION_STEP_LABELS[row.generation_step ?? ''] ?? 'Génération en cours'}… Une à deux
            minutes : tu peux quitter la page.
          </span>
        </p>
      )}

      {row && failed && (
        <div className="space-y-2">
          <p role="alert" className="text-error-600 flex items-start gap-2 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {stale
                ? 'La génération s’est interrompue.'
                : `La génération a échoué${row.generation_error ? ` : ${row.generation_error}` : '.'}`}
            </span>
          </p>
          <Button
            variant="outline"
            loading={generate.isPending}
            onClick={() => generate.mutate({ animation_id: row.id })}
          >
            <span className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4" aria-hidden /> Reprendre
            </span>
          </Button>
        </div>
      )}

      {compact && row && !running && (
        <div className="flex flex-wrap items-center gap-2">
          {/* après un échec, l'ancien brouillon reste là : on peut toujours le tester */}
          {row.draft != null && showTestLink && (
            <Link
              to="/tutor/admin/animations/$animationId"
              params={{ animationId: row.id }}
              className="bg-primary-500 hover:bg-primary-600 inline-flex min-h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold text-white"
            >
              <Play className="h-4 w-4" aria-hidden /> {testLabel}
            </Link>
          )}
          {row.draft != null && (
            <ModeButton active={mode === 'revise'} onClick={() => toggle('revise')}>
              <Wand2 className="h-4 w-4" aria-hidden /> Corriger avec l’IA…
            </ModeButton>
          )}
          <ModeButton active={open} onClick={() => toggle('regenerate')}>
            <RotateCcw className="h-4 w-4" aria-hidden /> Régénérer…
          </ModeButton>
          {actions}
        </div>
      )}
      {compact && row && !running && mode === 'revise' && reviseForm}
      {compact && row && !running && open && form('Régénérer l’animation')}

      {!compact && row && !running && row.draft != null && (
        <div className="flex flex-wrap items-center gap-2">
          {valid ? (
            <span className="bg-xp-100 text-xp-700 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Brouillon valide
            </span>
          ) : (
            <span className="bg-error-50 text-error-600 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> {errorCount} erreur
              {errorCount > 1 ? 's' : ''} à corriger
            </span>
          )}
          {showTestLink && (
            <Link
              to="/tutor/admin/animations/$animationId"
              params={{ animationId: row.id }}
              className="bg-primary-500 hover:bg-primary-600 inline-flex min-h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold text-white"
            >
              <Play className="h-4 w-4" aria-hidden /> {testLabel}
            </Link>
          )}
        </div>
      )}

      {!compact && row && !running && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {row.draft != null && (
              <ModeButton active={mode === 'revise'} onClick={() => toggle('revise')}>
                <Wand2 className="h-4 w-4" aria-hidden /> Corriger avec l’IA…
              </ModeButton>
            )}
            <ModeButton active={open} onClick={() => toggle('regenerate')}>
              <RotateCcw className="h-4 w-4" aria-hidden /> Régénérer…
            </ModeButton>
          </div>
          {mode === 'revise' && reviseForm}
          {open && form('Régénérer l’animation')}
        </div>
      )}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-expanded={active}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-11 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold',
        active
          ? 'border-primary-500 bg-primary-50 text-primary-700'
          : 'border-ink-200 text-ink-700 hover:bg-ink-50',
      )}
    >
      {children}
    </button>
  );
}

/** Scènes du brouillon (portée d'une correction). */
export function draftScenes(draft: unknown): { id: string; title: string; hora: string | null }[] {
  const scenes = (draft as { scenes?: unknown } | null)?.scenes;
  if (!Array.isArray(scenes)) return [];
  return scenes.flatMap((s) => {
    const x = s as { id?: unknown; title?: unknown; hora?: unknown };
    return typeof x.id === 'string'
      ? [
          {
            id: x.id,
            title: typeof x.title === 'string' ? x.title : x.id,
            hora: typeof x.hora === 'string' ? x.hora : null,
          },
        ]
      : [];
  });
}
